import {
  collection,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { AuditEventSchema, type AuditAction, type AuditEvent } from '@etiquetachef/types';
import { reportNonFatal, serializeError } from './errorReport';
import { firestore } from './firebase';

const TAG = 'admin/audit';

/** Evento de auditoria com id do doc e `at` em millis, para a UI. */
export type AuditEventRecord = AuditEvent & {
  /** Doc id (ULID) — não está no schema mas é útil em UI. */
  id: string;
  /** Server timestamp materializado em millis para formatação. */
  atMillis: number | null;
};

/** Página de eventos; `truncated` = bateu no `AUDIT_LIST_LIMIT`. */
export type AuditEventListResult = {
  events: AuditEventRecord[];
  truncated: boolean;
};

/** Teto de eventos por listener (sem paginação); a UI avisa quando trunca. */
export const AUDIT_LIST_LIMIT = 100;

/** Filtros aceitos; cada combinação depende de um índice composto de `/audit`. */
export type AuditFilters = {
  /** Filtra por target (tenant/user/billing) — usa índice composto existente. */
  targetType?: AuditEvent['targetType'];
  targetId?: string;
  /** Filtra por ação — usa índice composto action+at. */
  action?: AuditAction;
  /** Filtra por ator — usa índice composto actorUserId+at. */
  actorUserId?: string;
};

/** `Timestamp` (ou número) → millis; outro formato → `null`. */
function tsToMillis(at: unknown): number | null {
  if (typeof at === 'object' && at !== null && 'toMillis' in at) {
    const ts = at as { toMillis: () => number };
    return ts.toMillis();
  }
  if (typeof at === 'number') return at;
  return null;
}

/**
 * Assina os eventos de `/audit` (raiz) com filtros opcionais, `at desc`, até
 * `AUDIT_LIST_LIMIT`.
 *
 * Origem: sem issue — commit efa5a8ca2, 2026-04-26 (Sprint 2).
 *
 * Contrato: precedência dos filtros de alvo: `targetType+targetId` juntos;
 * senão `targetId` sozinho (todos os tipos). `action` e `actorUserId` somam.
 * Doc fora do `AuditEventSchema` é pulado; erro de snapshot vai para
 * `onError`.
 *
 * Usado por: `features/audit/useAuditEvents.ts` — que serve
 * `features/audit/AuditList.tsx` (`routes/AuditPage.tsx` e
 * `features/audit/TenantAuditTab.tsx`), `features/superAdmins/useSuperAdminAuditTrail.ts`
 * e `features/notifications/BroadcastHistory.tsx`.
 *
 * Arquitetura: rule `match /audit/{eventId}` (leitura super-admin; escrita só
 * pelas Functions). Índices em `firebase/firestore.indexes.json` (escopo
 * COLLECTION): `targetType+targetId+at`, `targetId+at`, `action+at`,
 * `actorUserId+at`.
 *
 * Dados: `audit/{ulid}` — `AuditEventSchema` (`action`, `targetType`,
 * `targetId`, `actorUserId`, `at` serverTimestamp, `before`/`after`).
 *
 * Falhas conhecidas:
 *  - Combinação de filtros sem índice próprio (ex.: `targetId` + `action`,
 *    `action` + `actorUserId`) cai em `failed-precondition` — hoje nenhuma
 *    tela a monta; a `AuditPage` registra o índice que faltaria.
 *  - Até 2026-10-13 evento fora do schema sumia só com `console.warn`, e a
 *    falha do listener só virava toast — sem report nenhum (#926, #939 item
 *    12). Agora `admin_audit_doc_dropped` (uma vez por doc) e
 *    `admin_audit_listen_failed`, pelo `reportNonFatal`.
 *
 * Dicas: filtro novo = índice novo no mesmo commit (CLAUDE.md, "Índices
 * Firestore"). O report de falha manda só os NOMES dos filtros ativos, não os
 * valores (o `targetId` é tenant, o `actorUserId` é pessoa).
 *
 * Histórico:
 *  - 2026-10-13 18:28 +0200 — (#939) report de doc descartado e de falha.
 */
export function subscribeAuditEvents(
  filters: AuditFilters,
  onResult: (r: AuditEventListResult) => void,
  onError: (err: unknown) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe filters=${JSON.stringify(filters)}`);
  const auditRef = collection(firestore, 'audit');

  // O índice "actorUserId + at" e "targetType + targetId + at" e "action + at"
  // já existem em firestore.indexes.json. Compomos a query priorizando
  // targetType+targetId quando ambos vêm (caso mais comum: aba do tenant);
  // depois action; depois actorUserId; senão lista global por at desc.
  const constraints = [];
  if (filters.targetType && filters.targetId) {
    constraints.push(where('targetType', '==', filters.targetType));
    constraints.push(where('targetId', '==', filters.targetId));
  } else if (filters.targetId) {
    // Aba "Auditoria" do tenant: queremos TODOS os eventos cujo alvo é o
    // tenant, independentemente do targetType. Eventos de lifecycle usam
    // targetType=TENANT e os de billing (PLAN_ACTIVATED, etc.) usam
    // targetType=BILLING — ambos com targetId = tenantId. Restringir a
    // TENANT escondia os de BILLING (a aba aparecia vazia). Requer o índice
    // audit(targetId ASC, at DESC).
    constraints.push(where('targetId', '==', filters.targetId));
  }
  if (filters.action) {
    constraints.push(where('action', '==', filters.action));
  }
  if (filters.actorUserId) {
    constraints.push(where('actorUserId', '==', filters.actorUserId));
  }

  const q = query(
    auditRef,
    ...constraints,
    orderBy('at', 'desc'),
    fsLimit(AUDIT_LIST_LIMIT),
  );

  // Doc já reportado por este listener: o snapshot se repete a cada mudança
  // na coleção, e o mesmo doc ruim não pode virar um report por emissão.
  const reportedDrops = new Set<string>();

  return onSnapshot(
    q,
    (snap) => {
      const events: AuditEventRecord[] = [];
      for (const doc of snap.docs) {
        const data = doc.data();
        const parsed = AuditEventSchema.safeParse(data);
        if (!parsed.success) {
          console.warn(`${TAG}: doc ${doc.id} ignorado`, parsed.error.issues);
          // Bug corrigido em 2026-10-13 18:28 +0200 (#939, item 12; #926):
          // o evento sumia da trilha de auditoria só com `console.warn`, que
          // ninguém lê em produção. Report uma vez por doc, com o campo que
          // falhou — nunca o conteúdo (`before`/`after` podem ter dado pessoal).
          if (!reportedDrops.has(doc.id)) {
            reportedDrops.add(doc.id);
            reportNonFatal('admin_audit_doc_dropped', {
              docId: doc.id,
              fields: parsed.error.issues.map((i) => i.path.join('.')).join(','),
            });
          }
          continue;
        }
        events.push({
          ...parsed.data,
          id: doc.id,
          atMillis: tsToMillis(parsed.data.at),
        });
      }
      onResult({ events, truncated: snap.docs.length >= AUDIT_LIST_LIMIT });
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      // Bug corrigido em 2026-10-13 18:28 +0200 (#939, item 12): a falha do
      // listener só virava toast na `AuditList` — sem report, um índice
      // faltando (`failed-precondition`) ou rule negando ficava invisível.
      reportNonFatal('admin_audit_listen_failed', {
        filters: Object.keys(filters).filter((k) => filters[k as keyof AuditFilters] !== undefined).join(','),
        ...serializeError(err),
      });
      onError(err);
    },
  );
}
